<template>
    <div class="brands-view-wrap">
        <v-card class="rounded_card quaroshadow mb-4" outlined>
            <v-card-title class="py-3">
                <div class="project-data-heading d-flex align-center">
                    <h2
                        class="project-data-heading__title"
                        style="font-size: 0.75rem !important; font-weight: 600 !important; color: rgba(15, 23, 42, 0.72); line-height: 1.2; text-transform: uppercase">
                        Top Brands per Platform
                    </h2>
                    <v-tooltip top max-width="360" color="primary">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey" style="vertical-align: middle">mdi-help-circle-outline</v-icon>
                        </template>
                        <span>Die Top-Marken je Plattform, basierend auf der aktuellen Visibility im gewählten Zeitraum.</span>
                    </v-tooltip>
                </div>
                <div class="flex-grow-1"></div>
                <span v-if="rangeLabel" class="data-basis-pill mr-3" :title="`Data basis: ${rangeLabel}`">
                    <v-icon x-small class="data-basis-pill__icon" aria-hidden="true">mdi-calendar-range</v-icon>
                    <span class="data-basis-pill__text">{{ rangeLabel }}</span>
                </span>
                <v-progress-circular v-if="topBrandsLoading" indeterminate size="18" width="2" color="primary" />
            </v-card-title>
            <v-card-text class="pt-0">
                <div v-if="topBrandsError" class="tbp-error">{{ topBrandsError }}</div>
                <div v-else class="tbp-grid">
                    <div v-for="panel in platformPanels" :key="panel.key" class="tbp-platform-card">
                        <div class="tbp-platform-head">
                            <span class="platform-icon-wrapper" v-if="panel.logo" style="display: flex; align-items: center">
                                <span class="platform-icon" v-html="panel.logo" style="margin: auto"></span>
                            </span>
                            <span class="tbp-platform-name">{{ panel.label }}</span>
                        </div>

                        <div v-if="panel.rows.length" class="tbp-rows">
                            <div
                                v-for="(row, idx) in panel.rows"
                                :key="panel.key + '-' + (row._id || row.canonicalName || row.brand_name || idx)"
                                class="tbp-row"
                                :class="{ 'tbp-row--primary': row.isPrimaryBrand }">
                                <div class="tbp-rank" :class="rankClass(row.rank)">#{{ row.rank || "-" }}</div>
                                <div class="tbp-brand-wrap">
                                    <span class="brand-color-dot" :style="topBrandDotStyle(row)"></span>
                                    <div class="tbp-brand-main">
                                        <div class="tbp-brand-name">
                                            <span class="tbp-brand-name-text">{{ displayBrandName(row) }}</span>
                                        </div>
                                        <div class="tbp-meta">{{ formatMentions(row.responsesWithMention) }} Mentions · Sent {{ formatSentiment(row.sentimentAvg) }}</div>
                                    </div>
                                </div>
                                <div class="tbp-metric-wrap">
                                    <div class="tbp-metric-value">{{ formatVisibility(row.visibilityPercent) }}%</div>
                                    <div v-if="row.visibilityDelta != null" class="tbp-change" :class="{ up: row.visibilityDelta > 0, down: row.visibilityDelta < 0 }">
                                        <v-icon x-small>{{ row.visibilityDelta > 0 ? "mdi-arrow-up" : row.visibilityDelta < 0 ? "mdi-arrow-down" : "mdi-minus" }}</v-icon>
                                        {{ formatSigned(row.visibilityDelta) }}
                                    </div>
                                    <div v-else class="tbp-change tbp-change--na">
                                        <v-icon x-small>mdi-minus</v-icon>
                                        —
                                    </div>
                                    <div class="tbp-bar">
                                        <div class="tbp-bar-fill" :style="{ width: row.visibilityPctForBar + '%' }"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="tbp-empty">No data</div>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <v-card class="rounded_card my-5 quaroshadow" style="border-top: 4px solid var(--v-primary-base); overflow: hidden">
            <v-toolbar id="projects-tool-bar" flat class="gradient-toolbar elevation-0">
                <div class="flex-grow-1"></div>
                <v-text-field
                    class="pr-5"
                    v-model="localSearch"
                    rounded
                    dense
                    filled
                    clearable
                    clear-icon="mdi-close"
                    prepend-inner-icon="mdi-magnify"
                    label="Search..."
                    single-line
                    hide-details
                    aria-label="Search table"
                    @input="onSearchInput" />
                <v-btn rounded color="primary" filled @click="exportLocalTable" aria-label="Export table">
                    <v-icon aria-hidden="true">mdi-cloud-download</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text class="px-0">
                <BrandsTable
                    ref="table"
                    :isActive="true"
                    :tags="tags"
                    :sentimentAvailable="sentimentAvailable"
                    :brands="brands"
                    :search="localSearch"
                    :loading="loading"
                    :totalPrompts="totalPrompts"
                    :project="project"
                    @searchUpdated="$emit('searchUpdated', $event)" />
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
    import BrandsTable from "../../../components/aisuite/BrandsTable.vue"
    import { getBrandColorFromName } from "../../../utils/brandColor"

    export default {
        name: "AIProjectBrandsView",
        components: { BrandsTable },
        props: {
            tags: { type: Array, default: () => [] },
            tagOperator: { type: String, default: "or" },
            sentimentAvailable: { type: Boolean, default: false },
            brands: { type: Array, default: () => [] },
            search: { type: String, default: "" },
            loading: { type: Boolean, default: false },
            totalPrompts: { type: Number, default: 0 },
            project: { type: Object, default: null },
            projectId: { type: [String, Number], default: "" },
            selectedDates: { type: Array, default: () => [] },
            selectedPlatform: { type: [Object, String], default: null },
            platformItems: { type: Array, default: () => [] },
            cachedTopBrandsSignature: { type: String, default: "" },
            cachedTopBrandsByPlatform: { type: Object, default: () => ({}) },
            dashboardMode: { type: String, default: "absolute" },
            rangeLabel: { type: String, default: "" }
        },
        data() {
            return {
                topBrandsLoading: false,
                topBrandsError: "",
                topBrandsByPlatform: {},
                topLimit: 5,
                _refreshDebounce: null,
                localSearch: ""
            }
        },
        computed: {
            topBrandsInputSignature() {
                try {
                    return JSON.stringify({
                        projectId: this.projectId,
                        tags: this.tags,
                        tagOperator: this.tagOperator,
                        selectedDates: this.selectedDates,
                        selectedPlatform: this.selectedPlatform,
                        platformKeys: this.effectivePlatformItems.map((p) => p.key),
                        dashboardMode: this.dashboardMode,
                        brandKeys: (Array.isArray(this.brands) ? this.brands : [])
                            .map((b) => (b?.canonicalName || b?.brand_name || (Array.isArray(b?.mentions) && b.mentions[0]) || "").toString().toLowerCase())
                            .filter(Boolean)
                            .sort()
                    })
                } catch (e) {
                    return ""
                }
            },
            effectivePlatformItems() {
                const base = Array.isArray(this.platformItems) ? this.platformItems.filter((p) => p && p.key) : []
                const selectedKey = this.selectedPlatform && typeof this.selectedPlatform === "object" ? this.selectedPlatform.key : null
                if (selectedKey && selectedKey !== "all") return base.filter((p) => p.key === selectedKey)
                return base
            },
            platformPanels() {
                return this.effectivePlatformItems.map((platform) => ({
                    key: platform.key,
                    label: platform.label || platform.name || platform.key,
                    logo: platform.logo || null,
                    rows: this.topBrandsByPlatform[platform.key] || []
                }))
            }
        },
        watch: {
            tags: {
                deep: true,
                handler() {
                    this.scheduleTopBrandsRefresh()
                }
            },
            tagOperator() {
                this.scheduleTopBrandsRefresh()
            },
            selectedDates: {
                deep: true,
                handler() {
                    this.scheduleTopBrandsRefresh()
                }
            },
            selectedPlatform: {
                deep: true,
                handler() {
                    this.scheduleTopBrandsRefresh()
                }
            },
            projectId() {
                this.scheduleTopBrandsRefresh()
            },
            dashboardMode() {
                this.scheduleTopBrandsRefresh()
            },
            brands: {
                deep: true,
                handler() {
                    this.scheduleTopBrandsRefresh()
                }
            },
            search: {
                immediate: true,
                handler(val) {
                    this.localSearch = val || ""
                }
            }
        },
        methods: {
            getPlatformDateField(platformKey) {
                const map = {
                    openai: "processed_dates_ai_tracking_openai",
                    chatgpt: "processed_dates_ai_tracking_chatgpt",
                    google: "processed_dates",
                    google_ai_mode: "processed_dates_ai_tracking_google_ai_mode",
                    gemini: "processed_dates_ai_tracking_gemini",
                    gemini_api: "processed_dates_ai_tracking_gemini_api",
                    claude: "processed_dates_ai_tracking_claude",
                    perplexity: "processed_dates_ai_tracking_perplexity",
                    grok: "processed_dates_ai_tracking_grok",
                    microsoft_copilot: "processed_dates_ai_tracking_microsoft_copilot",
                    amazon_rufus: "processed_dates_ai_tracking_amazon_rufus"
                }
                return map[platformKey] || "processed_dates_ai_tracking_openai"
            },
            resolveImportDatesForPlatform(platformKey) {
                const field = this.getPlatformDateField(platformKey)
                const arr = Array.isArray(this.project?.[field]) ? this.project[field] : []
                if (!arr.length) return []

                const selected = Array.isArray(this.selectedDates) ? this.selectedDates.filter(Boolean) : []
                if (!selected.length) return arr.slice(-2)

                const firstDay = selected[0]
                const lastDay = selected[1] || firstDay
                let earliest = null
                let latest = null

                arr.forEach((iso) => {
                    const dt = new Date(iso)
                    if (isNaN(dt)) return
                    const day = dt.toISOString().slice(0, 10)
                    if (day === firstDay && (!earliest || dt < earliest)) earliest = dt
                    if (day === lastDay && (!latest || dt > latest)) latest = dt
                })

                const out = []
                if (earliest) out.push(earliest.toISOString())
                if (latest && (!earliest || latest.getTime() !== earliest.getTime())) out.push(latest.toISOString())
                return out.length ? out : arr.slice(-2)
            },
            normalizeToken(value) {
                return (value || "")
                    .toString()
                    .normalize("NFKD")
                    .replace(/[\u2018\u2019\u0060\u00B4]/g, "'")
                    .replace(/['’`´]/g, "")
                    .replace(/\s+/g, " ")
                    .trim()
                    .toLowerCase()
            },
            isProjectBrandRow(row) {
                const projectToken = this.normalizeToken(this.project?.brand?.brand_name)
                if (!projectToken) return false

                const candidates = [row?.canonicalName, row?.brand_name]
                const rowMentions = Array.isArray(row?.mentions) ? row.mentions.map((m) => (typeof m === "string" ? m : m?.text?.content || m?.content || "")) : []
                candidates.push(...rowMentions)

                return candidates.some((value) => this.normalizeToken(value) === projectToken)
            },
            onSearchInput() {
                this.$emit("update:search", this.localSearch)
            },
            async exportLocalTable() {
                const csv = await this.exportTableData()
                if (!csv) return
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
                const link = document.createElement("a")
                link.href = URL.createObjectURL(blob)
                link.download = `brands_export_${new Date().toISOString().slice(0, 10)}.csv`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(link.href)
            },
            scheduleTopBrandsRefresh() {
                clearTimeout(this._refreshDebounce)
                this._refreshDebounce = setTimeout(() => this.refreshTopBrandsOverview(), 250)
            },
            resolveApiBase() {
                return (
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/brandvisibility/by/date"
                )
            },
            resolveApiRoot() {
                return JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                    this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                ) == -1
                    ? process.env.VUE_APP_MONITORING_API_URL
                    : process.env.VUE_APP_APIURL + "/monitoring-api"
            },
            resolveRangeForPlatform(platformKey) {
                const field = this.getPlatformDateField(platformKey)
                const arr = Array.isArray(this.project?.[field]) ? this.project[field] : []
                const toDay = (v) => {
                    const dt = new Date(v)
                    return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
                }
                const selected = Array.isArray(this.selectedDates) ? this.selectedDates.filter(Boolean) : []
                let from = selected.length ? selected[0] : null
                let to = selected.length ? selected[1] || selected[0] : null
                if ((!from || !to) && arr.length) {
                    const days = arr.map(toDay).filter(Boolean).sort()
                    if (days.length) {
                        from = from || days[0]
                        to = to || days[days.length - 1]
                    }
                }
                if (!from || !to) return null
                return { from, to }
            },
            buildSelectedBrandsPayload() {
                const list = Array.isArray(this.brands) ? this.brands : []
                return list.map((b) => ({
                    canonicalName: b?.canonicalName || b?.brand_name || null,
                    brand_name: b?.brand_name || b?.canonicalName || null,
                    brand_domain: b?.brand_domain || b?.metadata?.officialWebsite || b?.metadata?.website || null,
                    mentions: Array.isArray(b?.mentions) ? b.mentions : []
                }))
            },
            rowTokens(row) {
                const tokens = new Set()
                const push = (value) => {
                    const t = this.normalizeToken(value)
                    if (t) tokens.add(t)
                }
                push(row?.canonicalName)
                push(row?.brand_name)
                if (Array.isArray(row?.mentions)) {
                    row.mentions.forEach((m) => push(typeof m === "string" ? m : m?.text?.content || m?.content || ""))
                }
                return tokens
            },
            findMatchingRow(targetRow, candidateRows) {
                const list = Array.isArray(candidateRows) ? candidateRows : []
                if (!targetRow || !list.length) return null
                const targetTokens = this.rowTokens(targetRow)
                if (!targetTokens.size) return null
                return (
                    list.find((row) => {
                        const candTokens = this.rowTokens(row)
                        for (const tk of targetTokens) {
                            if (candTokens.has(tk)) return true
                        }
                        return false
                    }) || null
                )
            },
            async fetchPlatformTopBrands(platformKey) {
                if (!platformKey) return []
                let latestRows = []
                let firstRows = []
                let hasPrevious = false

                if (this.dashboardMode === "average") {
                    const range = this.resolveRangeForPlatform(platformKey)
                    if (!range) return []
                    const payload = {
                        company_id: localStorage.company_id,
                        project_id: this.projectId || this.project?._id,
                        platform: platformKey,
                        currentRange: range,
                        brandsData: this.buildSelectedBrandsPayload(),
                        tagsOperator: this.tagOperator,
                        tags: Array.isArray(this.tags) && this.tags.length ? this.tags : null
                    }
                    const res = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/brandvisibility/range-averages", payload)
                    const brands = Array.isArray(res?.data?.brands) ? res.data.brands : []
                    latestRows = brands.map((b) => ({
                        canonicalName: b.canonicalName,
                        brand_name: b.brand_name || b.canonicalName,
                        brand_domain: b.brand_domain || null,
                        metadata: b.metadata || null,
                        mentions: Array.isArray(b.mentions) ? b.mentions : [],
                        visibilityPercent: typeof b.visibilityPercent === "number" ? b.visibilityPercent : null,
                        sentimentAvg: typeof b.sentimentAvg === "number" ? b.sentimentAvg : null,
                        responsesWithMention: b.responsesWithMention || 0
                    }))
                    firstRows = brands.map((b) => ({
                        canonicalName: b.canonicalName,
                        brand_name: b.brand_name || b.canonicalName,
                        mentions: Array.isArray(b.mentions) ? b.mentions : [],
                        visibilityPercent: typeof b.visibilityPercentOld === "number" ? b.visibilityPercentOld : null
                    }))
                    hasPrevious = brands.some((b) => typeof b.visibilityPercentOld === "number")
                } else {
                    const importDates = this.resolveImportDatesForPlatform(platformKey)
                    if (!importDates.length) return []

                    const payload = {
                        company_id: localStorage.company_id,
                        project_id: this.projectId || this.project?._id,
                        platform: platformKey,
                        importDates,
                        selectedBrands: this.buildSelectedBrandsPayload(),
                        tagsOperator: this.tagOperator,
                        tags: Array.isArray(this.tags) && this.tags.length ? this.tags : null
                    }

                    const res = await this.$helpers.axiosPost(this.resolveApiBase(), payload)
                    const byDate = Array.isArray(res?.data?.brandsByDate) ? res.data.brandsByDate : []
                    firstRows = byDate.length > 1 ? byDate[0] : []
                    const latest = byDate.length ? byDate[byDate.length - 1] : []
                    latestRows = Array.isArray(latest) ? latest : []
                    hasPrevious = byDate.length > 1
                }

                const rows = Array.isArray(latestRows) ? latestRows : []
                const sorted = rows
                    .filter((row) => row && typeof row.visibilityPercent === "number" && Number.isFinite(row.visibilityPercent))
                    .sort((a, b) => (b.visibilityPercent || 0) - (a.visibilityPercent || 0))
                const limit = Math.max(0, Number(this.topLimit) || 0)
                const getRowRank = (targetRow) => {
                    if (!targetRow) return null
                    const idx = sorted.findIndex((row) => this.findMatchingRow(targetRow, [row]))
                    return idx >= 0 ? idx + 1 : null
                }

                let primaryRow = sorted.find((row) => this.isProjectBrandRow(row))
                if (!primaryRow) {
                    primaryRow = {
                        canonicalName: this.project?.brand?.brand_name || "Primary Brand",
                        brand_name: this.project?.brand?.brand_name || "Primary Brand",
                        brand_domain: this.project?.brand?.brand_domain || null,
                        responsesWithMention: 0,
                        sentimentAvg: null,
                        visibilityPercent: 0
                    }
                }

                let resultRows = []
                if (limit > 0) {
                    const topRows = sorted.slice(0, limit)
                    const containsPrimary = topRows.some((row) => this.isProjectBrandRow(row))
                    if (containsPrimary) {
                        resultRows = topRows
                    } else {
                        const topWithoutPrimary = sorted.filter((row) => !this.isProjectBrandRow(row)).slice(0, Math.max(0, limit - 1))
                        resultRows = [...topWithoutPrimary, primaryRow]
                    }
                }

                const maxVisibility = Math.max(1, ...resultRows.map((r) => r.visibilityPercent || 0))
                return resultRows.map((row) => ({
                    ...(hasPrevious
                        ? {
                              visibilityDelta: (() => {
                                  const prev = this.findMatchingRow(row, firstRows)
                                  const prevVisibility = typeof prev?.visibilityPercent === "number" ? prev.visibilityPercent : 0
                                  const currentVisibility = typeof row?.visibilityPercent === "number" ? row.visibilityPercent : 0
                                  return Number((currentVisibility - prevVisibility).toFixed(2))
                              })()
                          }
                        : { visibilityDelta: null }),
                    ...row,
                    rank: getRowRank(row),
                    isPrimaryBrand: this.isProjectBrandRow(row),
                    visibilityPctForBar: Math.max(8, Math.min(100, ((row.visibilityPercent || 0) / maxVisibility) * 100))
                }))
            },
            async refreshTopBrandsOverview() {
                if (!this.projectId || !this.effectivePlatformItems.length || !Array.isArray(this.selectedDates) || !this.selectedDates.filter(Boolean).length) {
                    this.topBrandsByPlatform = {}
                    this.topBrandsError = ""
                    this.topBrandsLoading = false
                    return
                }

                this.topBrandsLoading = true
                this.topBrandsError = ""
                try {
                    const entries = await Promise.all(
                        this.effectivePlatformItems.map(async (platform) => {
                            const rows = await this.fetchPlatformTopBrands(platform.key)
                            return [platform.key, rows]
                        })
                    )
                    this.topBrandsByPlatform = Object.fromEntries(entries)
                    this.$emit("topBrandsLoaded", {
                        signature: this.topBrandsInputSignature,
                        byPlatform: this.topBrandsByPlatform
                    })
                } catch (e) {
                    this.topBrandsByPlatform = {}
                    this.topBrandsError = "Top Brands konnten nicht geladen werden."
                } finally {
                    this.topBrandsLoading = false
                }
            },
            displayBrandName(row) {
                const mentionFallback = Array.isArray(row?.mentions) && row.mentions.length ? String(row.mentions[0]) : ""
                return row?.canonicalName || row?.brand_name || mentionFallback || "Unknown"
            },
            topBrandDotStyle(row) {
                const name = this.displayBrandName(row)
                return { background: getBrandColorFromName(name) }
            },
            getBrandFaviconDomain(row) {
                const website = row?.metadata?.officialWebsite || row?.brand_domain || ""
                if (website)
                    return String(website)
                        .replace(/^https?:\/\//, "")
                        .replace(/^www\./, "")
                        .split("/")[0]
                if (Array.isArray(row?.mentions) && row.mentions.length) {
                    const domain = String(row.mentions[0])
                        .replace(/^https?:\/\//, "")
                        .replace(/^www\./, "")
                        .split("/")[0]
                    if (/^[\w-]+\.[a-z]{2,}$/i.test(domain)) return domain
                }
                return null
            },
            formatVisibility(value) {
                const n = Number(value)
                if (!Number.isFinite(n)) return "0.00"
                return n.toFixed(2)
            },
            formatSigned(value) {
                const n = Number(value)
                if (!Number.isFinite(n)) return "—"
                return `${n > 0 ? "+" : ""}${n.toFixed(2)}%`
            },
            formatMentions(value) {
                const n = Number(value)
                if (!Number.isFinite(n)) return "0"
                return n.toLocaleString("de-DE")
            },
            formatSentiment(value) {
                const n = Number(value)
                if (!Number.isFinite(n)) return "—"
                const pct = Math.max(-100, Math.min(100, n * 100))
                return `${pct > 0 ? "+" : ""}${pct.toFixed(0)}`
            },
            rankClass(rank) {
                if (rank === 1) return "is-gold"
                if (rank === 2) return "is-silver"
                if (rank === 3) return "is-bronze"
                return ""
            },
            async exportTableData() {
                const table = this.$refs.table
                if (table && typeof table.exportTableData === "function") {
                    return table.exportTableData()
                }
                return ""
            }
        },
        mounted() {
            if (
                this.cachedTopBrandsSignature &&
                this.cachedTopBrandsSignature === this.topBrandsInputSignature &&
                this.cachedTopBrandsByPlatform &&
                Object.keys(this.cachedTopBrandsByPlatform).length
            ) {
                this.topBrandsByPlatform = { ...this.cachedTopBrandsByPlatform }
                return
            }
            this.scheduleTopBrandsRefresh()
        }
    }
</script>

<style scoped>
    .tbp-grid {
        display: grid;
        gap: 12px;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    }
    .tbp-platform-card {
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        padding: 10px;
        background: rgba(255, 255, 255, 0.6);
        overflow: hidden;
    }
    .tbp-platform-head {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 700;
        margin-bottom: 8px;
    }
    .tbp-platform-name {
        font-size: 13px;
        color: rgba(15, 23, 42, 0.86);
    }
    .tbp-rows {
        display: grid;
        gap: 8px;
    }
    .tbp-row {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) minmax(96px, 0.8fr);
        gap: 8px;
        align-items: center;
        border: 1px solid rgba(15, 23, 42, 0.06);
        background: #fff;
        border-radius: 10px;
        padding: 8px;
        min-width: 0;
        max-width: 100%;
    }
    .tbp-row--primary {
        border-color: color-mix(in srgb, var(--v-primary-base) 45%, rgba(15, 23, 42, 0.06));
        background: color-mix(in srgb, var(--v-primary-base) 7%, #ffffff);
    }
    .tbp-rank {
        font-size: 11px;
        font-weight: 700;
        border-radius: 999px;
        padding: 3px 8px;
        color: #4b5563;
        background: rgba(107, 114, 128, 0.14);
        border: 1px solid rgba(107, 114, 128, 0.22);
    }
    .tbp-rank.is-gold {
        color: #166534;
        background: rgba(34, 197, 94, 0.18);
        border-color: rgba(34, 197, 94, 0.4);
    }
    .tbp-rank.is-silver {
        color: #854d0e;
        background: rgba(250, 204, 21, 0.2);
        border-color: rgba(250, 204, 21, 0.42);
    }
    .tbp-rank.is-bronze {
        color: #9a3412;
        background: rgba(249, 115, 22, 0.18);
        border-color: rgba(249, 115, 22, 0.4);
    }
    .tbp-brand-wrap {
        display: flex;
        align-items: center;
        min-width: 0;
        gap: 8px;
    }
    .brand-color-dot {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 2px;
        box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(0, 0, 0, 0.08);
        position: relative;
        top: 1px;
        flex: 0 0 auto;
    }
    .tbp-brand-main {
        min-width: 0;
        overflow: hidden;
    }
    .tbp-brand-name {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        font-weight: 700;
        min-width: 0;
    }
    .tbp-brand-name-text {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1 1 auto;
    }
    .tbp-primary-badge {
        font-size: 10px;
        font-weight: 700;
        border-radius: 999px;
        padding: 2px 6px;
        color: var(--v-primary-base);
        background: color-mix(in srgb, var(--v-primary-base) 14%, transparent);
        border: 1px solid color-mix(in srgb, var(--v-primary-base) 35%, transparent);
        flex: 0 0 auto;
    }
    .tbp-meta {
        font-size: 11px;
        color: rgba(55, 65, 81, 0.78);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .tbp-metric-wrap {
        min-width: 0;
    }
    .tbp-metric-value {
        font-size: 12px;
        font-weight: 700;
        text-align: right;
        margin-bottom: 2px;
    }
    .tbp-change {
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        gap: 2px;
        width: 100%;
        font-size: 11px;
        font-weight: 600;
        margin-bottom: 4px;
        color: rgba(75, 85, 99, 0.9);
    }
    .tbp-change.up {
        color: #1f9d45;
    }
    .tbp-change.down {
        color: #d64545;
    }
    .tbp-change--na {
        color: rgba(75, 85, 99, 0.62);
    }
    .tbp-bar {
        width: 100%;
        height: 8px;
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.08);
        overflow: hidden;
    }
    .tbp-bar-fill {
        height: 100%;
        border-radius: 999px;
        background: linear-gradient(90deg, var(--v-primary-base), color-mix(in srgb, var(--v-primary-base) 62%, #ffffff));
    }
    .tbp-empty {
        font-size: 12px;
        color: rgba(55, 65, 81, 0.7);
        border: 1px dashed rgba(15, 23, 42, 0.16);
        border-radius: 10px;
        padding: 10px;
        text-align: center;
    }
    .tbp-error {
        color: #b91c1c;
        font-size: 12px;
        margin-bottom: 6px;
    }

    .data-basis-pill {
        display: inline-flex;
        align-items: center;
        align-self: center;
        flex-shrink: 0;
        gap: 5px;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        line-height: 1;
        color: var(--v-primary-base, #1976d2);
        background: rgba(25, 118, 210, 0.08);
        border: 1px solid rgba(25, 118, 210, 0.18);
        white-space: nowrap;
    }
    .data-basis-pill__icon {
        color: var(--v-primary-base, #1976d2) !important;
    }
    .data-basis-pill__text {
        letter-spacing: 0.2px;
    }
    .theme--dark .data-basis-pill {
        color: #90caf9;
        background: rgba(144, 202, 249, 0.12);
        border-color: rgba(144, 202, 249, 0.25);
    }
    .theme--dark .data-basis-pill__icon {
        color: #90caf9 !important;
    }

    .theme--dark .tbp-platform-card {
        background: rgba(255, 255, 255, 0.03);
        border-color: rgba(255, 255, 255, 0.12);
    }
    .theme--dark .tbp-platform-name {
        color: rgba(255, 255, 255, 0.9);
    }
    .theme--dark .tbp-row {
        background: rgba(255, 255, 255, 0.03);
        border-color: rgba(255, 255, 255, 0.1);
    }
    .theme--dark .tbp-row--primary {
        border-color: color-mix(in srgb, var(--v-primary-base) 55%, rgba(255, 255, 255, 0.1));
        background: color-mix(in srgb, var(--v-primary-base) 16%, rgba(255, 255, 255, 0.03));
    }
    .theme--dark .brand-color-dot {
        box-shadow: 0 0 0 2px #1f1f1f, 0 0 0 3px rgba(255, 255, 255, 0.08);
    }
    .theme--dark .tbp-brand-name {
        color: rgba(255, 255, 255, 0.92);
    }
    .theme--dark .tbp-primary-badge {
        color: #bfdbfe;
        background: color-mix(in srgb, var(--v-primary-base) 26%, transparent);
        border-color: color-mix(in srgb, var(--v-primary-base) 42%, transparent);
    }
    .theme--dark .tbp-meta {
        color: rgba(255, 255, 255, 0.72);
    }
    .theme--dark .tbp-metric-value {
        color: rgba(255, 255, 255, 0.88);
    }
    .theme--dark .tbp-change {
        color: rgba(255, 255, 255, 0.6);
    }
    .theme--dark .tbp-change.up {
        color: #4fd06e;
    }
    .theme--dark .tbp-change.down {
        color: #ff7c7c;
    }
    .theme--dark .tbp-change--na {
        color: rgba(255, 255, 255, 0.5);
    }
    .theme--dark .tbp-bar {
        background: rgba(255, 255, 255, 0.16);
    }
    .theme--dark .tbp-empty {
        color: rgba(255, 255, 255, 0.75);
        border-color: rgba(255, 255, 255, 0.22);
    }

    @media (max-width: 960px) {
        .tbp-row {
            grid-template-columns: auto 1fr;
            grid-template-areas:
                "rank brand"
                "metric metric";
        }
        .tbp-rank {
            grid-area: rank;
        }
        .tbp-brand-wrap {
            grid-area: brand;
        }
        .tbp-metric-wrap {
            grid-area: metric;
        }
        .tbp-brand-name {
            flex-wrap: wrap;
        }
        .tbp-brand-name-text {
            flex: 1 1 100%;
        }
    }
</style>
